'use client';

import { cn } from '@pulsex/utils';
import { authorCardHref } from '@/utils/links';
import Link from 'next/link';

type AuthorSearchCardProps = {
    data: {
        id?: string | number;
        title?: string;
        url?: string | null;
        publicUrl?: string | null;
        image?: { path?: string } | string | null;
        summary?: string;
    };
    className?: string;
};

export const AuthorSearchCard = ({ data, className }: AuthorSearchCardProps) => {
    if (!data?.id || !data?.title) return null;

    const imageSrc =
        typeof data.image === 'string'
            ? data.image
            : data.image?.path || '/images/fallbackAuthor.png';

    const href = authorCardHref(data);

    const content = (
        <>
            <div className="relative w-28 h-28 overflow-hidden rounded-full mb-3 bg-white p-1.5">
                <img
                    src={imageSrc}
                    alt={`${data.title} profile image`}
                    className="w-full h-full rounded-full object-cover"
                />
            </div>
            <div className="text-2xl leading-7 text-black group-hover:text-masterRed transition-colors">
                {data.title}
            </div>
            {data.summary ? (
                <p className="mt-2 text-sm text-[#4c4c4c] line-clamp-3">{data.summary}</p>
            ) : null}
        </>
    );

    const classes = cn(
        'flex flex-col items-center text-center bg-lightgreyBackground p-4 group',
        className
    );

    // Without a trustworthy destination the card still carries the writer's name, photo and
    // bio - so show it, but don't make it a link to the wrong page.
    if (!href) {
        return <div className={classes}>{content}</div>;
    }

    return (
        <Link href={href} className={classes} aria-label={data.title}>
            {content}
        </Link>
    );
};

export default AuthorSearchCard;
